import { CardProps } from "./types";
import { useIntl } from "react-intl";
import {ExpandableValue} from "./expandable-value/component";
import _ from "lodash";
import { Link } from "react-router-dom";
import CountryContext from "../../../CountryContext";
import {useContext} from "react";

export const Card = ({
  survey,
  colors,
  actions,
  extraFields,
  hideAuthor = false,
}: CardProps) => {

  // context
  const {country} = useContext(CountryContext)

  // hooks
  const intl = useIntl();

  // NOTE: cards can have both KCC and KColor data (e.g. Deumidificazione)
  const isKColor =
    (!!survey.kcolor_color && !_.isEmpty(survey.kcolor_color))
    || (!!survey.kcolor_product && !_.isEmpty(survey.kcolor_product))
  ;
  const isKCC = !!survey.color && !_.isEmpty(survey.color);

  // utils
  const kcolorCodes = Array.isArray(survey.kcolor_color)
    ? survey.kcolor_color.map(c => c?.code).filter(Boolean)
    : survey.kcolor_color?.code
      ? [survey.kcolor_color.code]
      : [];
  const commons = {
    color: _.flattenDeep([survey.color, kcolorCodes]).filter(Boolean),
    surface: _.flattenDeep([survey.surface || [], survey.kcolor_surface || []]).filter(Boolean),
  }
  const data: {label: string, value: any, isColor?: boolean}[] = [
    // ----- FIXED
    // {label: "ID", value: survey.id},
    {label: intl.formatMessage({id: "worksite", defaultMessage: "Cantiere"}), value: survey.worksite_name && (
      <Link
        to={`/${country.code}/worksites/${survey.worksite_id}`}
        target="_blank" rel="noopener noreferrer"
        className="text-am-700 hover:text-am-500 underline"
      >
        {survey.worksite_name}
      </Link>
    )},
    {label: intl.formatMessage({id: "name", defaultMessage: "Nome"}), value: survey.name ?? survey.survey_name},
    {label: intl.formatMessage({id: "color", defaultMessage: "Colore"}), value: commons.color, isColor: true},
    {label: intl.formatMessage({id: "surface", defaultMessage: "Superficie"}), value: Array.isArray(commons.surface)
      ? commons.surface.filter(Boolean).map(s => `${s} mq`)
      : !!commons.surface ? `${commons.surface} mq` : null,
    },
    ...(isKCC
      ? [
        {label: intl.formatMessage({id: "system", defaultMessage: "Sistema"}), value: survey.system},
        {label: intl.formatMessage({id: "primers", defaultMessage: "Primers"}), value: survey.primers},
        {label: intl.formatMessage({id: "reti", defaultMessage: "Reti"}), value: survey.reti},
        {label: intl.formatMessage({id: "rasanti", defaultMessage: "Rasanti"}), value: survey.rasanti},
      ]
      : []
    ),
    ...(isKColor
      ? [
        {
          label: intl.formatMessage({id: "product", defaultMessage: "Prodotto"}),
          value: Array.isArray(survey.kcolor_product)
            ? survey.kcolor_product.map((p: any) => p!.des_prod_comm)
            : survey.kcolor_product!.des_prod_comm,
        },
        {
          label: intl.formatMessage({id: "product_package", defaultMessage: "Confezione"}),
          value: Array.isArray(survey.kcolor_product_package)
            ? survey.kcolor_product_package.filter(Boolean).map((p, idx) =>
              // @ts-ignore
              `${p} ${survey.kcolor_product[idx].base_um}`
            )
            : !!survey.kcolor_product_package ? `${survey.kcolor_product_package} ${survey.kcolor_product!.base_um}` : null,
        },
      ]
      : []
    ),
    {
      label: intl.formatMessage({id: "created", defaultMessage: "Creato"}),
      value: (
        <div>
          <p>{intl.formatDate(survey.created_at, {year: "numeric", month: "2-digit", day: "2-digit"})}</p>
          {!hideAuthor && <p>{survey.created_by || "N/A"}</p>}
        </div>
      ),
    },
    // ----- EXTRA
    ...Object.entries(extraFields ?? {}).map(([label, {value, render}]) => ({
      label,
      value: render?.(survey) || value,
    })),
  ];

  return (
    <div className="bg-white p-4 flex flex-col gap-3 h-72 max-h-72 md:h-80 md:max-h-80" data-id-survey={survey.id}>
      <h2 className="text-base leading-tight">
        {survey.service_name}
      </h2>
      <div className="flex flex-col gap-1.5 overflow-y-auto flex-1 pr-0.5">
        {data.map(({label, value, isColor}, idx) => {
          if ((!value && value !== 0) || (Array.isArray(value) && _.isEmpty(_.flattenDeep(value))))
            return null;
          return (
            <div key={idx} className="flex flex-row gap-4 items-start">
              <div className="w-1/3">
                {label}
              </div>
              <div className="w-2/3">
                <ExpandableValue value={value} isColor={isColor} {...(isColor ? {colors} : {})}/>
              </div>
            </div>
          )
        })}
      </div>
      <div className="border-t border-black-300"/>
      <div className="flex justify-end gap-5">
        {actions}
      </div>
    </div>
  )
}